/**
 * snap — AK-321 v1.1. Прилипание вершин редактора к границам полей.
 *
 * Screen-space hit-test (НЕ геодезический порог): порог задаётся в пикселях
 * (founder: 10-12px), поэтому работает одинаково на всех зумах. Сравниваем
 * курсор/вершину с спроецированными точками рёбер полей.
 *
 * Приоритет: вершина границы поля приоритетнее точки на ребре (founder lock).
 * Кандидаты — кольца полей, чьи bbox пересекаются с viewport (см.
 * SnapIndex.candidatesInView).
 *
 * Перф (ADR-038): проекции вершин полей кэшируются и пересчитываются только на
 * `moveend` (камера сменилась) — см. SnapIndex.reproject. Сам lookup —
 * O(точки видимых полей), бьётся в <2ms бюджет за счёт bbox-фильтра.
 *
 * Чистая геометрия экранных координат — MapLibre инжектится как project/bbox
 * функции, чтобы модуль был тестируемым.
 */
import type { FieldGeometry, LngLat } from './fieldGeojson';

export interface ScreenPt {
  x: number;
  y: number;
}

/** Результат snap: lng/lat точки прилипания + тип (вершина приоритетнее). */
export interface SnapHit {
  lngLat: LngLat;
  kind: 'vertex' | 'edge';
  /** Дистанция до курсора в пикселях (для выбора ближайшего среди кандидатов). */
  distPx: number;
}

/** Спроецированное кольцо: экранные точки + исходные lngLat (для edge-проекции). */
interface ProjectedRing {
  screen: ScreenPt[];
  lngLat: LngLat[];
}

/**
 * Индекс полей для snap. Держит геометрии-кандидаты и их экранные проекции,
 * пересчитываемые на moveend. Один экземпляр на сессию редактора.
 */
export class SnapIndex {
  /** Порог прилипания, px (founder: 10-12). */
  private thresholdPx: number;
  /** Геометрии полей-кандидатов (по field_id). */
  private fields = new Map<string, FieldGeometry>();
  /** Кэш экранных проекций колец (валиден до следующего reproject). */
  private projected: ProjectedRing[] = [];

  constructor(thresholdPx = 11) {
    this.thresholdPx = thresholdPx;
  }

  /** Заменить набор полей-кандидатов (после загрузки геометрий во вьюпорте). */
  setFields(fields: FieldGeometry[]): void {
    this.fields.clear();
    for (const f of fields) this.fields.set(f.fieldId, f);
  }

  /** Добавить одно поле (по мере подгрузки геометрий). */
  addField(field: FieldGeometry): void {
    this.fields.set(field.fieldId, field);
  }

  /** Есть ли вообще кандидаты. */
  get size(): number {
    return this.fields.size;
  }

  /**
   * Пересчитать экранные проекции колец (вызывать на `moveend`). Только для
   * полей, чьи bbox пересекают текущий вьюпорт (viewportBounds в lng/lat).
   */
  reproject(
    project: (ll: LngLat) => ScreenPt,
    viewportBounds: [number, number, number, number],
  ): void {
    this.projected = [];
    for (const f of this.fields.values()) {
      if (!bboxIntersects(f.bbox, viewportBounds)) continue;
      for (const ring of f.outerRings) {
        this.projected.push({
          screen: ring.map(project),
          lngLat: ring,
        });
      }
    }
  }

  /**
   * Hit-test экранной точки против рёбер полей. Возвращает точку прилипания
   * или null, если ничего ближе порога. Вершина приоритетнее ребра.
   */
  snap(cursor: ScreenPt): SnapHit | null {
    const t = this.thresholdPx;
    let bestVertex: SnapHit | null = null;
    let bestEdge: SnapHit | null = null;

    for (const ring of this.projected) {
      const pts = ring.screen;
      // Вершины.
      for (let i = 0; i < pts.length; i++) {
        const d = dist(cursor, pts[i]);
        if (d <= t && (!bestVertex || d < bestVertex.distPx)) {
          bestVertex = { lngLat: ring.lngLat[i], kind: 'vertex', distPx: d };
        }
      }
      // Рёбра (только если вершина не найдена ближе — но считаем всегда, дёшево).
      for (let i = 1; i < pts.length; i++) {
        const proj = projectOnSegment(cursor, pts[i - 1], pts[i]);
        if (proj.distPx <= t && (!bestEdge || proj.distPx < bestEdge.distPx)) {
          // Интерполируем lng/lat по параметру t вдоль ребра.
          const a = ring.lngLat[i - 1];
          const b = ring.lngLat[i];
          bestEdge = {
            lngLat: [a[0] + (b[0] - a[0]) * proj.t, a[1] + (b[1] - a[1]) * proj.t],
            kind: 'edge',
            distPx: proj.distPx,
          };
        }
      }
    }
    // Вершина приоритетнее ребра (founder lock), даже если ребро чуть ближе.
    return bestVertex ?? bestEdge;
  }
}

function dist(a: ScreenPt, b: ScreenPt): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/** Проекция точки p на отрезок ab: {distPx, t∈[0,1]}. */
function projectOnSegment(
  p: ScreenPt,
  a: ScreenPt,
  b: ScreenPt,
): { distPx: number; t: number } {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const len2 = abx * abx + aby * aby;
  if (len2 === 0) return { distPx: dist(p, a), t: 0 };
  let t = ((p.x - a.x) * abx + (p.y - a.y) * aby) / len2;
  t = Math.max(0, Math.min(1, t));
  const cx = a.x + abx * t;
  const cy = a.y + aby * t;
  return { distPx: Math.hypot(p.x - cx, p.y - cy), t };
}

/** Пересекаются ли два bbox [minLng,minLat,maxLng,maxLat]. */
function bboxIntersects(
  a: [number, number, number, number],
  b: [number, number, number, number],
): boolean {
  return a[0] <= b[2] && a[2] >= b[0] && a[1] <= b[3] && a[3] >= b[1];
}
